// API service for Process Optimizer

import { Industry, Role, Question, QuestionSet, WritingSuggestion, Document, Report } from '../types';
import { getApiUrl, getApiTimeout } from '../config/environments';

class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

class ApiService {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = getApiUrl();
    this.timeout = getApiTimeout();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if token exists
    const token = localStorage.getItem('auth_token');
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Implement timeout using AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let errorData: Record<string, any> = {};
        let errorMessage = 'An error occurred';
        
        try {
          const text = await response.text();
          if (text) {
            errorData = JSON.parse(text);
            errorMessage = errorData.detail || errorData.message || `HTTP ${response.status} error`;
          } else {
            errorMessage = `HTTP ${response.status} error`;
          }
        } catch {
          errorMessage = `HTTP ${response.status} error`;
        }
        
        throw new ApiError(
          errorMessage,
          response.status,
          errorData
        );
      }

      // Handle 204 No Content (empty response)
      if (response.status === 204) {
        return undefined as T;
      }

      // Try to parse JSON, but handle empty responses
      const text = await response.text();
      if (!text) {
        return undefined as T;
      }
      
      try {
        return JSON.parse(text);
      } catch {
        return undefined as T;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Handle timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError(
          `Request timeout after ${this.timeout}ms`,
          408,
          { timeout: this.timeout }
        );
      }
      
      throw new ApiError(
        'Network error occurred',
        0,
        error
      );
    }
  }

  // Industry endpoints
  async getIndustries(): Promise<Industry[]> {
    const result = await this.request<any[]>('/industries');
    
    // Transform snake_case to camelCase
    return result.map((industry: any) => ({
      id: industry.id,
      name: industry.name,
      description: industry.description,
      website: industry.website,
      icon: industry.icon,
      themeColor: industry.theme_color,
      roles: industry.roles.map((role: any) => ({
        id: role.id,
        name: role.name,
        description: role.description,
        industryId: role.industry_id,
        tasks: role.tasks
      }))
    }));
  }

  async getRolesByIndustry(industryId: string): Promise<Role[]> {
    const result = await this.request<any[]>(`/industries/${industryId}/roles`);
    
    // Transform snake_case to camelCase
    return result.map((role: any) => ({
      id: role.id,
      name: role.name,
      description: role.description,
      industryId: role.industry_id,
      tasks: role.tasks
    }));
  }

  async getQuestionsByIndustryRole(industryId: string, roleId: string): Promise<QuestionSet> {
    const result = await this.request<any>(`/industries/${industryId}/roles/${roleId}/questions`);
    
    // Transform snake_case to camelCase
    return {
      id: result.id,
      industryId: result.industry_id,
      roleId: result.role_id,
      questions: result.questions.map((q: any) => ({
        id: q.id,
        text: q.text,
        category: q.category,
        industryId: q.industry_id,
        roleId: q.role_id,
        order: q.order,
        isRequired: q.is_required,
        minWords: q.min_words,
        maxWords: q.max_words,
        hints: q.hints,
        isAiGenerated: q.is_ai_generated
      })),
      totalQuestions: result.total_questions
    };
  }

  async getQuestionsWithAI(industryId: string, roleId: string): Promise<QuestionSet> {
    const result = await this.request<any>(`/industries/${industryId}/roles/${roleId}/questions/with-ai`);
    
    // Transform snake_case to camelCase
    return {
      id: result.id,
      industryId: result.industry_id,
      roleId: result.role_id,
      questions: result.questions.map((q: any) => ({
        id: q.id,
        text: q.text,
        category: q.category,
        industryId: q.industry_id,
        roleId: q.role_id,
        order: q.order,
        isRequired: q.is_required,
        minWords: q.min_words,
        maxWords: q.max_words,
        hints: q.hints,
        isAiGenerated: q.is_ai_generated
      })),
      totalQuestions: result.total_questions
    };
  }

  async generateAdditionalQuestions(
    industryId: string, 
    roleId: string, 
    initialResponses: Array<{questionId: string, questionText: string, answer: string, category: string}>
  ): Promise<QuestionSet> {
    const result = await this.request<any>(
      `/industries/${industryId}/roles/${roleId}/questions/generate-additional`,
      {
        method: 'POST',
        body: JSON.stringify(initialResponses)
      }
    );
    
    // Transform snake_case to camelCase
    return {
      id: result.id,
      industryId: result.industry_id,
      roleId: result.role_id,
      questions: result.questions.map((q: any) => ({
        id: q.id,
        text: q.text,
        category: q.category,
        industryId: q.industry_id,
        roleId: q.role_id,
        order: q.order,
        isRequired: q.is_required,
        minWords: q.min_words,
        maxWords: q.max_words,
        hints: q.hints,
        isAiGenerated: q.is_ai_generated
      })),
      totalQuestions: result.total_questions
    };
  }

  async regenerateSingleQuestion(
    industryId: string,
    roleId: string,
    question: Question
  ): Promise<Question> {
    const result = await this.request<any>(
      `/industries/${industryId}/roles/${roleId}/questions/regenerate-single`,
      {
        method: 'POST',
        body: JSON.stringify({
          id: question.id,
          text: question.text,
          category: question.category,
          industry_id: question.industryId,
          role_id: question.roleId,
          order: question.order,
          is_required: question.isRequired,
          min_words: question.minWords,
          max_words: question.maxWords,
          hints: question.hints,
          is_ai_generated: question.isAiGenerated
        })
      }
    );
    
    // Transform snake_case to camelCase
    return {
      id: result.id,
      text: result.text,
      category: result.category,
      industryId: result.industry_id,
      roleId: result.role_id,
      order: result.order,
      isRequired: result.is_required,
      minWords: result.min_words,
      maxWords: result.max_words,
      hints: result.hints,
      isAiGenerated: result.is_ai_generated
    };
  }

  async getWritingSuggestions(
    questionId: string,
    industryId: string,
    roleId: string,
    currentAnswer: string = ""
  ): Promise<{ questionId: string; suggestions: WritingSuggestion[] }> {
    const result = await this.request<any>(
      `/assessments/questions/${questionId}/writing-suggestions?industry_id=${industryId}&role_id=${roleId}&current_answer=${encodeURIComponent(currentAnswer)}`,
      {
        method: 'POST'
      }
    );
    
    return {
      questionId: result.question_id,
      suggestions: result.suggestions.map((suggestion: any) => ({
        title: suggestion.title,
        content: suggestion.content,
        example: suggestion.example
      }))
    };
  }

  async uploadDocument(
    assessmentId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Document> {
    if (!assessmentId) {
      throw new ApiError('Assessment identifier is required for uploads', 400);
    }

    return new Promise<Document>((resolve, reject) => {
      const url = `${this.baseUrl}/documents/upload`;
      const xhr = new XMLHttpRequest();
      xhr.timeout = this.timeout;
      xhr.open('POST', url);

      const token = localStorage.getItem('auth_token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (event: ProgressEvent<EventTarget>) => {
        if (!event.lengthComputable || !onProgress) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve({
              id: response.id || response.document_id || response._id || '',
              assessmentId: response.assessment_id || assessmentId,
              filename: response.filename || file.name,
              filePath: response.file_path || response.filePath || '',
              extractedText: response.extracted_text || response.extractedText,
              uploadedAt: response.uploaded_at || response.uploadedAt || new Date().toISOString()
            });
          } catch (error) {
            reject(new ApiError('Invalid response from upload service', xhr.status));
          }
        } else {
          let errorData: any = {};
          try {
            errorData = JSON.parse(xhr.responseText);
          } catch (error) {
            // If response is not JSON, try to get text
            try {
              errorData = { detail: xhr.responseText || 'Document upload failed' };
            } catch {
              errorData = { detail: 'Document upload failed' };
            }
          }
          
          // Handle 409 Conflict (duplicate document)
          if (xhr.status === 409) {
            // errorData might be a dict with 'detail' containing the message and existing_document
            const detail = errorData.detail || errorData;
            const message = typeof detail === 'string' 
              ? detail 
              : detail.message || 'A document with identical content already exists';
            
            reject(
              new ApiError(
                message,
                xhr.status,
                typeof detail === 'object' ? detail : errorData
              )
            );
          } else {
            reject(
              new ApiError(
                errorData.detail || errorData.message || 'Document upload failed',
                xhr.status,
                errorData
              )
            );
          }
        }
      };

      xhr.onerror = () => {
        reject(new ApiError('Network error occurred during upload', 0));
      };

      xhr.ontimeout = () => {
        reject(new ApiError(
          `Upload timeout after ${this.timeout}ms`,
          408,
          { timeout: this.timeout }
        ));
      };

      const formData = new FormData();
      formData.append('assessment_id', assessmentId);
      formData.append('file', file);

      xhr.send(formData);
    });
  }

  async deleteDocument(documentId: string): Promise<void> {
    if (!documentId) {
      throw new ApiError('Document ID is required', 400);
    }

    await this.request<void>(
      `/documents/${documentId}`,
      {
        method: 'DELETE',
      }
    );
    // 204 No Content means success, no need to return anything
  }

  // Report endpoints
  async generateReport(assessmentId: string, includeDocuments: boolean = true): Promise<Report> {
    const result = await this.request<any>('/reports/generate', {
      method: 'POST',
      body: JSON.stringify({
        assessment_id: assessmentId,
        include_documents: includeDocuments
      })
    });

    // Transform snake_case to camelCase
    return {
      id: result.id,
      assessmentId: result.assessment_id,
      title: result.title,
      executiveSummary: result.executive_summary,
      keyFindings: result.key_findings || [],
      recommendations: result.recommendations || [],
      sections: (result.sections || []).map((section: any) => ({
        title: section.title,
        content: section.content,
        order: section.order
      })),
      diagrams: (result.diagrams || []).map((diagram: any) => ({
        title: diagram.title,
        description: diagram.description,
        mermaidCode: diagram.mermaid_code,
        processType: diagram.process_type,
        order: diagram.order
      })),
      generatedAt: result.generated_at,
      status: result.status
    };
  }

  async getReport(reportId: string): Promise<Report> {
    const result = await this.request<any>(`/reports/${reportId}`);

    // Transform snake_case to camelCase
    return {
      id: result.id,
      assessmentId: result.assessment_id,
      title: result.title,
      executiveSummary: result.executive_summary,
      keyFindings: result.key_findings || [],
      recommendations: result.recommendations || [],
      sections: (result.sections || []).map((section: any) => ({
        title: section.title,
        content: section.content,
        order: section.order
      })),
      diagrams: (result.diagrams || []).map((diagram: any) => ({
        title: diagram.title,
        description: diagram.description,
        mermaidCode: diagram.mermaid_code,
        processType: diagram.process_type,
        order: diagram.order
      })),
      generatedAt: result.generated_at,
      status: result.status
    };
  }

  async getReportsByAssessment(assessmentId: string): Promise<Report[]> {
    const result = await this.request<any[]>(`/reports/assessment/${assessmentId}`);

    // Transform snake_case to camelCase
    return result.map((report: any) => ({
      id: report.id,
      assessmentId: report.assessment_id,
      title: report.title,
      executiveSummary: report.executive_summary,
      keyFindings: report.key_findings || [],
      recommendations: report.recommendations || [],
      sections: (report.sections || []).map((section: any) => ({
        title: section.title,
        content: section.content,
        order: section.order
      })),
      diagrams: (report.diagrams || []).map((diagram: any) => ({
        title: diagram.title,
        description: diagram.description,
        mermaidCode: diagram.mermaid_code,
        processType: diagram.process_type,
        order: diagram.order
      })),
      generatedAt: report.generated_at,
      status: report.status
    }));
  }

  // ============================================================================
  // ADMIN ENDPOINTS
  // ============================================================================

  /**
   * Get all assessments for admin dashboard
   * @param statusFilter Optional filter by lead status (new, reviewing, contacted, etc.)
   */
  async getAdminAssessments(statusFilter?: string): Promise<{
    assessments: Array<{
      id: string;
      user_id: string;
      user_email?: string;
      company_name?: string;
      industry?: string;
      role?: string;
      status: string;
      lead_status: string;
      created_at: string;
      has_report: boolean;
      urgency_score?: number;
    }>;
    total: number;
    filtered: number;
  }> {
    const params = statusFilter ? `?status_filter=${statusFilter}` : '';
    const result = await this.request<any>(`/reports/admin/assessments${params}`);
    return result;
  }

  /**
   * Get complete report and assessment data for admin view
   * @param assessmentId Assessment ID to retrieve
   */
  async getAdminReport(assessmentId: string): Promise<{
    assessment: any;
    report: any;
    internal_notes: Array<{
      content: string;
      created_by: string;
      created_at: string;
    }>;
  }> {
    const result = await this.request<any>(`/reports/admin/reports/${assessmentId}`);
    return result;
  }

  /**
   * Add an internal note to an assessment/report
   * @param assessmentId Assessment ID to add note to
   * @param content Note content
   */
  async addInternalNote(assessmentId: string, content: string): Promise<{
    content: string;
    created_by: string;
    created_at: string;
  }> {
    const result = await this.request<any>(
      `/reports/admin/reports/${assessmentId}/notes`,
      {
        method: 'POST',
        body: JSON.stringify({ content })
      }
    );
    return result;
  }

  /**
   * Update the lead status of an assessment
   * @param assessmentId Assessment ID to update
   * @param status New status (new, reviewing, contacted, proposal_sent, won, lost)
   */
  async updateAssessmentStatus(assessmentId: string, status: string): Promise<{
    status: string;
    assessment_id: string;
    new_status: string;
  }> {
    const result = await this.request<any>(
      `/reports/admin/assessments/${assessmentId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }
    );
    return result;
  }
}

export const apiService = new ApiService();
export default apiService;
